import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query'
import { useTokenAndOrgId, processPaginatedData, InferenceAPI } from './utils'
import { ListNotificationsArgs, Notification } from './inference.gen'

const fetchNotifications = async (
  args: ListNotificationsArgs
): Promise<{
  instances: Array<Notification>
  hasNextPage: boolean
  total: number
}> => {
  const result = await InferenceAPI.listNotifications(args)

  if (!result || !result.notifications) {
    throw new Error('Invalid response from server')
  }

  return {
    instances: result.notifications,
    hasNextPage: result.total === args.page_size,
    total: result.total,
  }
}

export const useListNotifications = (pageSize = 5) => {
  const { orgId, token } = useTokenAndOrgId()

  return useInfiniteQuery({
    queryKey: ['list-notifications', orgId, token, pageSize],
    enabled: !!orgId && !!token,
    queryFn: ({ pageParam = 1 }) =>
      fetchNotifications({
        page_number: pageParam,
        page_size: pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasNextPage ? allPages.length + 1 : undefined,
    select: (data) => {
      const { allInstances: allNotifications, total } =
        processPaginatedData<Notification>(data.pages)
      return { allNotifications, total }
    },
  })
}

export const useTotalUnreadCount = () => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery<number, Error>({
    queryKey: ['total-unread-count', orgId, token],
    queryFn: async () => {
      if (!orgId || !token) return 0

      const result = await fetchNotifications({
        page_number: 1,
        page_size: 1,
        unread: true,
      })

      return result.total
    },
    enabled: !!orgId && !!token,
    staleTime: 1000 * 60,
  })
}

const markNotificationRead = async (notificationId: number) => {
  const result = await InferenceAPI.markNotificationRead({
    notification_id: notificationId,
  })

  return result.ok
}

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient()
  const { token, orgId } = useTokenAndOrgId()

  return useMutation({
    mutationFn: async (notificationId: number) =>
      markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['list-notifications', orgId, token],
      })
      queryClient.invalidateQueries({
        queryKey: ['total-unread-count', orgId, token],
      })
    },
  })
}

const fetchAllNotifications = async (
  unRead: boolean = false
): Promise<Notification[]> => {
  let notifications: Notification[] = []
  let page = 1
  const hasMorePages = true
  while (hasMorePages) {
    const result = await fetchNotifications({
      page_number: page,
      unread: unRead,
      page_size: 30,
    })
    notifications = notifications.concat(result.instances)
    if (!result.hasNextPage) break
    page++
  }
  return notifications
}

const markAllNotificationsRead = async (): Promise<void> => {
  const unreadNotifications = await fetchAllNotifications(true)
  const unreadIds = unreadNotifications.map((n) => n.notification_id)
  if (unreadIds.length === 0) return

  await Promise.all(unreadIds.map(markNotificationRead))
}

export const useMarkAllNotificationsRead = (): UseMutationResult<
  void,
  Error,
  void,
  unknown
> => {
  const queryClient = useQueryClient()
  const { token, orgId } = useTokenAndOrgId()

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await markAllNotificationsRead()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['list-notifications', orgId, token],
      })
      queryClient.invalidateQueries({
        queryKey: ['total-unread-count', orgId, token],
      })
    },
  })
}
